<template>
  <section id="specifications" class="bg-white pb-[30px]" v-bind="$attrs">
    <div class="container">
      <h1
        :class="
          !props.hasProductIntroduction && !props.productTopFeatures
            ? 'mt-16'
            : ''
        "
        class="mb-[10px] md:mb-6 text-[26px] leading-[34px] md:text-2xl lg:text-[33px] lg:leading-[48px] italic text-black font-myriad-pro font-semibold text-center uppercase break-words px-4 pt-6"
      >
        {{ $t("common.tech_specs") }}
      </h1>
      <div class="flex justify-center gap-3 mt-8 mb-12 sm:gap-8 md:justify-end">
        <button
          class="min-w-[145px] w-full md:w-auto text-base flex items-center justify-between min-h-[45px] border border-solid border-gray-400 rounded-full px-4 py-2 overflow-hidden hover:opacity-50"
          @click="collapseAll"
        >
          {{ $t("common.collapse_all") }}
          <ChevronUp class="inline w-6 h-6 ltr:ml-2 rtl:mr-2" />
        </button>
        <button
          class="min-w-[145px] w-full md:w-auto text-sm md:text-base flex items-center justify-between min-h-[45px] border border-solid border-gray-400 rounded-full px-4 py-2 overflow-hidden hover:opacity-50"
          @click="expandAll"
        >
          {{ $t("common.expand_all") }}
          <ChevronDown class="inline w-6 h-6 ltr:ml-2 rtl:mr-2" />
        </button>
      </div>
      <div v-for="(attributes, groupName, index) in items" :key="attributes.id">
        <Collapsible
          ref="collapsible"
          :aria-label="groupName"
          class="relative mb-[26px] md:mb-0 border-b border-gray-200 border-solid md:border-none"
          :is-expanded="index === 0"
        >
          <template #trigger="{ isActive }">
            <div class="customTrigger">
              <div
                class="max-h-[45px] flex justify-between items-center min-w-full md:border-b md:border-solid border-gray-200 lg:px-4 pb-[18px] md:mb-5 lg:mb-8"
              >
                <h2
                  class="text-[24px] lg:text-[28px] lg:leading-[32px] uppercase font-myriad-pro-condensed font-normal leading-tight text-[#404040] flex items-center"
                >
                  {{ groupName }}
                </h2>
                <CloseIcon
                  v-if="isActive"
                  class="inline w-6 h-6 md:w-8 md:h-8 text-[#404040]"
                />
                <PlusIcon
                  v-else
                  class="inline w-6 h-6 md:w-8 md:h-8 text-[#404040]"
                />
              </div>
            </div>
          </template>
          <ul class="specifications__container">
            <li class="ml-0">
              <div class="w-full md:mb-[62px]">
                <div
                  v-for="attribute in attributes"
                  :key="attribute.id"
                  class="inline-flex min-w-full mb-4 lg:pl-36"
                >
                  <div
                    class="md:max-w-[90%] flex md:block items-start justify-between w-full gap-4 md:gap-0"
                  >
                    <h4
                      class="my-0 text-[16px] md:text-[20px] font-myriad-pro font-semibold italic lg:leading-[28px] text-[#404040]"
                    >
                      {{ attribute.name }}
                    </h4>
                    <p
                      class="md:mb-4 text-sm md:text-[18px] md:leading-[28px] font-normal font-myriad-pro max-w-[180px] md:max-w-full text-right md:text-left text-[#404040]"
                    >
                      {{ attribute.values.map((v) => v.content).join(", ") }}
                    </p>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </Collapsible>
      </div>
    </div>
  </section>
</template>

<script setup>
import { groupBy } from "lodash-es";
import ChevronUp from "@/assets/svg/heroicons/chevron-up.svg?component";
import ChevronDown from "@/assets/svg/heroicons/chevron-down.svg?component";
import PlusIcon from "@/assets/svg/heroicons/plus.svg?component";
import CloseIcon from "@/assets/svg/heroicons/close.svg?component";

const props = defineProps({
  productSlug: {
    type: [Array, Object],
    required: true,
  },
  hasProductIntroduction: {
    type: Boolean,
    required: true,
  },
  productTopFeatures: {
    type: Array,
    required: true,
  },
});

const items = computed(() => {
  return groupBy(props.productSlug, "group");
});
const collapsible = ref(null);

onBeforeMount(() => {});

function collapseAll() {
  collapsible.value.map((element) => {
    element.updateValue(false);
    return element;
  }); // eslint-disable-line no-return-assign
}
function expandAll() {
  collapsible.value.map((element) => {
    element.updateValue(true);
    return element;
  }); // eslint-disable-line no-return-assign
}
</script>

<style lang="postcss" scoped>
.specifications__container {
  column-count: 1;
  grid-column-gap: 0;
  column-gap: 0;

  .specifications__data {
    width: 100%;

    .specifications__attributes {
      display: inline-flex;
      min-width: 100%;
      margin-bottom: 2rem;
      border-bottom: 1px solid #f5f5f5;

      span {
        display: block;
        min-width: 65px;

        img {
          position: relative;
          bottom: 4px;
        }
      }

      h4 {
        text-transform: uppercase;
        font-size: 24px;
        margin-bottom: 0;
      }

      p {
        font-size: 22px;
      }
    }
  }
}

@media only screen and (min-width: 768px) {
  .specifications__container {
    column-count: 2;
    grid-column-gap: 60px;
    column-gap: 60px;
  }
}
</style>
